import { createInterface } from 'node:readline'
import { spawn } from 'node:child_process'
import { api, driftlessHome, getCachedWorkspace, saveWorkspaceToConfig, persistConfig, upsertWorkspace } from '../api-client'
import { analyticsCommandError, analyticsEvent, flushAndExit } from '../analytics'

function openBrowser(url: string) {
  const cmd = process.platform === 'darwin' ? 'open' :
    process.platform === 'win32' ? 'start' : 'xdg-open'
  const child = spawn(cmd, [url], { stdio: 'ignore', detached: true })
  child.unref()
}

export async function loginCommand(args: string[]): Promise<void> {
  // --key flag for quick setup (CI, scripts)
  const keyIndex = args.indexOf('--key')
  if (keyIndex !== -1 && args[keyIndex + 1]) {
    const apiKey = args[keyIndex + 1]
    if (!apiKey.startsWith('drift_')) {
      console.error('Error: API key must start with "drift_".')
      console.error('Get a valid key from your Driftless Dashboard → Settings → API Keys.')
      const cachedSlug = getCachedWorkspace().slug
      if (cachedSlug) {
        await analyticsEvent('cli_login', cachedSlug, { success: false })
        await analyticsCommandError('login', cachedSlug, 'API key must start with drift_')
      }
      await flushAndExit(1)
    }
    await saveConfig(apiKey)
    return
  }

  const apiUrl = process.env['DRIFTLESS_API_URL'] || 'https://api.driftless.icu/api/v1'
  // The dashboard (sign-in + API keys) lives on the app subdomain; the apex is
  // the public marketing site. Overridable for staging/self-host.
  const dashboardUrl = process.env['DRIFTLESS_DASHBOARD_URL'] || 'https://app.driftless.icu'

  console.log('Opening Driftless Dashboard to get your API key...')
  console.log()

  // Open browser to dashboard API keys section
  openBrowser(dashboardUrl)

  console.log('If the browser doesn\'t open, visit:')
  console.log(`  ${dashboardUrl}`)
  console.log()
  console.log('1. Sign in with your account')
  console.log('2. Go to Settings → API Keys')
  console.log('3. Create a new API key')
  console.log('4. Copy the key (starts with drift_)')
  console.log()

  // Interactive prompt
  const rl = createInterface({
    input: process.stdin,
    output: process.stdout,
  })

  const apiKey = await new Promise<string>((resolve) => {
    rl.question('Paste your API key: ', (answer) => {
      rl.close()
      resolve(answer.trim())
    })
  })

  if (!apiKey.startsWith('drift_')) {
    console.error('Error: API key must start with "drift_".')
    console.error('Get a valid key from your Driftless Dashboard → Settings → API Keys.')
    const cachedSlug = getCachedWorkspace().slug
    if (cachedSlug) {
      await analyticsEvent('cli_login', cachedSlug, { success: false })
      await analyticsCommandError('login', cachedSlug, 'API key must start with drift_')
    }
    await flushAndExit(1)
  }

  await saveConfig(apiKey, apiUrl)
}

async function saveConfig(apiKey: string, apiUrl?: string) {
  const url = apiUrl || 'https://api.driftless.icu/api/v1'

  try {
    // persistConfig writes config.json owner-only (dir 0700, file 0600).
    persistConfig({ api_key: apiKey, api_url: url })
  } catch (err) {
    console.error('Failed to save config:', err)
    const cachedSlug = getCachedWorkspace().slug
    if (cachedSlug) {
      await analyticsEvent('cli_login', cachedSlug, { success: false })
      await analyticsCommandError('login', cachedSlug, err)
    }
    await flushAndExit(1)
  }

  console.log()
  console.log('Logged in successfully.')
  console.log(`  Config: ${driftlessHome()}/config.json`)

  // Resolve + cache the workspace identity now so resolveRepo has a durable
  // fallback if /me flakes on a later command. Non-blocking by design.
  try {
    const me = (await api.get('/me', { timeoutMs: 8000, retries: 1 })) as {
      slug?: string
      workspace_id?: string
      name?: string
    }
    if (me?.slug) {
      saveWorkspaceToConfig(me.slug, me.workspace_id)
      // Register this workspace so `driftless workspace list/switch` can see it.
      upsertWorkspace({ slug: me.slug, id: me.workspace_id, name: me.name, api_key: apiKey })
      persistConfig({ active_workspace: me.slug })
      console.log(`  Workspace: ${me.slug}`)
      analyticsEvent('cli_login', me.slug, { success: true })

      // Scoped credentials: one key can be authorized for several workspaces.
      // Register every workspace you belong to under THIS key, so `workspace
      // switch` works across all of them without a second login. Switching to a
      // workspace the key isn't scoped to returns a clear 403 at use time.
      try {
        const mine = (await api.get('/me/workspaces', { timeoutMs: 8000, retries: 1 })) as Array<{
          slug?: string
          id?: string
          workspace_id?: string
          name?: string
        }>
        let registered = 0
        for (const w of mine ?? []) {
          if (!w?.slug || w.slug === me.slug) continue
          upsertWorkspace({ slug: w.slug, id: w.id ?? w.workspace_id, name: w.name, api_key: apiKey })
          registered++
        }
        if (registered > 0) {
          console.log(`  +${registered} more workspace${registered === 1 ? '' : 's'} registered — switch with: driftless workspace switch <slug>`)
        }
      } catch {
        // Non-blocking: the active workspace is set; others can be added later.
      }
    }
  } catch (err) {
    const cachedSlug = getCachedWorkspace().slug
    if (cachedSlug) {
      analyticsEvent('cli_login', cachedSlug, { success: false })
      analyticsCommandError('login', cachedSlug, err)
    }
    // slug will be cached on the first successful command instead
  }

  console.log()
  console.log('Next: driftless install-skill   — teaches your agent the Driftless workflow')
  console.log('Then just work: pull context before editing, persist what you learn after.')
  await flushAndExit(0)
}
